import { apiFetch, apiFetchFormData } from "@/auth/apiClient";

export type Community = {
  id: string;
  courseId: string;
  name: string;
};

export type CommunityPost = {
  id: string;
  communityId: string;
  userId: number;
  content: string;
  parentId: string | null;
  createdAt?: string;
  /** Prefer this for relative time when present (from API / socket). */
  createdAtMs?: number;
  updatedAt?: string | null;
  updatedAtMs?: number | null;
  attachmentUrl?: string | null;
  attachmentType?: "IMAGE" | "VIDEO" | "PDF" | "AUDIO" | null;
  user?: {
    id: number;
    email: string;
    profile?: {
      firstName?: string | null;
      lastName?: string | null;
      image?: string | null;
    } | null;
  };
};

export type CommunityPostsResponse = {
  items: CommunityPost[];
  total: number;
  page: number;
  limit: number;
};

export type CommunityMemberRow = {
  userId: number;
  email: string;
};

export async function getCourseCommunity(courseId: string): Promise<Community> {
  return apiFetch<Community>(`/lms/courses/${courseId}/community`, {
    method: "GET",
    timeoutMs: 15000,
  });
}

export async function getCourseCommunityPosts(params: {
  courseId: string;
  pagination?: { page?: number; limit?: number };
  parentId?: string | null;
}): Promise<CommunityPostsResponse> {
  const page = params.pagination?.page ?? 1;
  const limit = params.pagination?.limit ?? 10;

  const qs = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    ...(params.parentId ? { parentId: params.parentId } : {}),
  });

  return apiFetch<CommunityPostsResponse>(
    `/lms/courses/${params.courseId}/community/posts?${qs.toString()}`,
    { method: "GET", timeoutMs: 15000 },
  );
}

export async function getCourseCommunityMembers(courseId: string): Promise<CommunityMemberRow[]> {
  return apiFetch<CommunityMemberRow[]>(
    `/lms/courses/${courseId}/community/members`,
    { method: "GET", timeoutMs: 15000 },
  );
}

export async function addCourseCommunityMember(courseId: string, userId: number): Promise<void> {
  await apiFetch(
    `/lms/courses/${courseId}/community/members`,
    { method: "POST", timeoutMs: 15000, json: { userId } },
  );
}

export async function removeCourseCommunityMember(courseId: string, userId: number): Promise<void> {
  await apiFetch(
    `/lms/courses/${courseId}/community/members/${userId}`,
    { method: "DELETE", timeoutMs: 15000 },
  );
}

export async function deleteCourseCommunityPost(
  courseId: string,
  postId: string,
): Promise<{ removed: boolean }> {
  return apiFetch<{ removed: boolean }>(`/lms/courses/${courseId}/community/posts/${postId}`, {
    method: "DELETE",
    timeoutMs: 15000,
  });
}

export async function updateCourseCommunityPost(
  courseId: string,
  postId: string,
  body: { content: string },
): Promise<CommunityPost> {
  return apiFetch<CommunityPost>(`/lms/courses/${courseId}/community/posts/${postId}`, {
    method: "PATCH",
    timeoutMs: 15000,
    json: body,
  });
}

export type CommunityPostAttachmentUploadResponse = {
  url: string;
  attachmentType: "IMAGE" | "VIDEO" | "PDF" | "AUDIO";
};

export async function uploadCourseCommunityPostAttachment(
  courseId: string,
  file: File,
): Promise<CommunityPostAttachmentUploadResponse> {
  const formData = new FormData();
  formData.append("file", file);
  return apiFetchFormData<CommunityPostAttachmentUploadResponse>(
    `/lms/courses/${courseId}/community/posts/attachments`,
    formData,
  );
}

